'use client';

import { motion, useInView } from 'framer-motion';
import { ReactNode, useRef } from 'react';

interface ScrollRevealProps {
  children: ReactNode;
  width?: 'fit-content' | '100%';
  delay?: number;
  duration?: number;
  direction?: 'up' | 'down' | 'left' | 'right' | 'none';
  distance?: number;
  once?: boolean;
}

export const ScrollReveal = ({
  children,
  width = '100%',
  delay = 0,
  duration = 0.6,
  direction = 'up',
  distance = 30,
  once = true,
}: ScrollRevealProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once });

  const getVariants = () => {
    const hidden = {
      opacity: 0,
      scale: 0.98,
      x: direction === 'left' ? distance : direction === 'right' ? -distance : 0,
      y: direction === 'up' ? distance : direction === 'down' ? -distance : 0,
    };

    const visible = {
      opacity: 1,
      scale: 1,
      x: 0,
      y: 0,
    };

    return { hidden, visible };
  };

  const variants = getVariants();

  return (
    <div ref={ref} style={{ position: 'relative', width, overflow: 'visible' }}>
      <motion.div
        variants={variants}
        initial="hidden"
        animate={isInView ? 'visible' : 'hidden'}
        transition={{
          duration,
          delay,
          ease: [0.21, 0.47, 0.32, 0.98], // Custom elegant cubic-bezier
        }}
      >
        {children}
      </motion.div>
    </div>
  );
};
